/**
 * Altyazı hazırlık durumları (Faz 3).
 *
 * Sunucu ön hazırlama kuyruğu sayesinde çoğu iz hazırdır. Bu kanca:
 *   - öğe açılınca hazırlamayı tetikler (idempotent),
 *   - iz başına `ready/preparing/idle/burn_required` durumunu getirir,
 *   - hazırlanan iş varken 3 saniyede bir yoklar.
 */
import { useEffect, useState } from 'react';
import { api, type SubtitleState } from './api.ts';

export type SubtitleStates = Record<string, SubtitleState>;

const POLL_INTERVAL_MS = 3_000;

export function useSubtitleStatus(itemId: string): SubtitleStates {
  const [states, setStates] = useState<SubtitleStates>({});

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const read = async (): Promise<boolean> => {
      try {
        const value = await api.subtitleStatus(itemId);
        if (cancelled) return false;
        const next: SubtitleStates = {};
        for (const stream of value.streams) next[stream.key] = stream.state;
        setStates(next);
        return value.streams.some((stream) => stream.state === 'preparing');
      } catch {
        return false;
      }
    };

    const poll = async (): Promise<void> => {
      const active = await read();
      if (!cancelled && active) {
        timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
      }
    };

    void (async () => {
      // Öğe açıldığında hazırlamayı garanti et; sonra durumu oku.
      await api.prepareSubtitles(itemId).catch(() => undefined);
      if (!cancelled) await poll();
    })();

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [itemId]);

  return states;
}
